import { useState } from "react";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { SubmissionButton } from "@thalamos/frontend";
import type {
  ExtendedVisionUser,
  UpdateUserProfileRequestUnbranded,
  UserId,
} from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import { PopupDialog } from "../../Dialog/PopupDialog.js";
import { TypographyI18N } from "../../TypographyI18N/TypographyI18N.js";

export interface EditUserProfileModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    userId: UserId,
    updateUserData: UpdateUserProfileRequestUnbranded,
  ) => Promise<void>;
  user: ExtendedVisionUser;
}

export function EditUserProfileModal({
  open,
  onClose,
  onConfirm,
  user,
}: EditUserProfileModalProps) {
  const { t } = useTranslation();
  const [updatedUser, setUpdatedUser] =
    useState<UpdateUserProfileRequestUnbranded>({
      name: user.name,
      email: user.email,
      shoulderNumber: user.shoulderNumber || "",
    });
  const isValid =
    updatedUser.name.trim().length > 0 && updatedUser.email.trim().length > 0;
  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableDialogRootPadding={true}
    >
      <DialogTitle>
        {t("admin.users.profileModal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          edge="end"
          sx={{ position: "absolute", right: 15, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
          }}
        >
          <TextField
            label="Name"
            value={updatedUser.name}
            onChange={(e) => {
              setUpdatedUser({
                ...updatedUser,
                name: e.target.value,
              });
            }}
          />
          <TextField
            label={t("admin.users.profileModal.email")}
            value={updatedUser.email}
            onChange={(e) => {
              setUpdatedUser({
                ...updatedUser,
                email: e.target.value,
              });
            }}
          />
          <TextField
            label={t("admin.users.profileModal.shoulderNumber")}
            value={updatedUser.shoulderNumber}
            onChange={(e) => {
              setUpdatedUser({
                ...updatedUser,
                shoulderNumber: e.target.value,
              });
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
        }}
      >
        <Button onClick={onClose} color="primary">
          <TypographyI18N translationKey={tk("common.close")} />
        </Button>
        <SubmissionButton
          data-testid="edit-user-modal-confirm-button"
          onClick={async () => {
            await onConfirm(user.id, updatedUser);
          }}
          color="primary"
          variant="contained"
          disabled={!isValid}
        >
          {t("admin.users.profileModal.update")}
        </SubmissionButton>
      </DialogActions>
    </PopupDialog>
  );
}
